import * as React from "react";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import { useInView } from "@/hooks/useInView";

const PLANS = [
  {
    id: "start",
    name: "Website-Abo",
    tagline: "Für alle, die sauber starten wollen.",
    price: "59",
    pages: "5 Unterseiten",
    features: [
      "Individuelles Design statt Baukasten",
      "Hosting, SSL & Wartung inklusive",
      "Optimiert für alle Geräte",
      "SEO-Grundstruktur",
      "Persönlicher Ansprechpartner",
    ],
    cta: "Kostenlosen Entwurf anfragen",
    featured: false,
  },
  {
    id: "business",
    name: "Business",
    tagline: "Mehr Inhalte, mehr Sichtbarkeit, mehr Anfragen.",
    price: "79",
    pages: "10 Unterseiten",
    features: [
      "Alles aus dem Website-Abo",
      "Landingpage für Kampagnen",
      "Kontaktformular & Terminbuchung",
      "Google Business Profil gepflegt",
      "Monatliche Performance-Übersicht",
    ],
    cta: "Business anfragen",
    featured: true,
  },
  {
    id: "premium",
    name: "Premium Enterprise",
    tagline: "Für Marken, die dauerhaft vorne stehen wollen.",
    price: "129",
    pages: "15 Unterseiten",
    features: [
      "Alles aus dem Business-Paket",
      "Laufendes SEO- & Ranking-Tracking",
      "Monatlich neue Blogposts",
      "Content-Updates auf Zuruf",
      "Prioritäts-Support innerhalb 24 h",
    ],
    cta: "Premium anfragen",
    featured: false,
  },
];

const PlanCard = ({ plan, index, inView }) => (
  <article
    className={`relative flex h-full flex-col border p-8 transition-[opacity,transform,box-shadow,border-color] duration-700 ease-out sm:p-9 ${
      plan.featured
        ? "border-[hsl(222_80%_58%/0.55)] bg-[hsl(0_0%_100%/0.95)] shadow-[0_40px_80px_-50px_hsl(225_60%_30%/0.75)] lg:-mt-6 lg:mb-6"
        : "border-light-border bg-[hsl(0_0%_100%/0.78)] shadow-[0_28px_60px_-45px_hsl(225_40%_25%/0.6)]"
    } ${inView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"} hover:-translate-y-1.5 hover:shadow-[0_44px_86px_-48px_hsl(225_50%_28%/0.7)]`}
    style={{ transitionDelay: `${index * 120}ms` }}
  >
    {plan.featured && (
      <span
        className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-gradient-cta px-3 py-1 font-display text-[10px] font-medium uppercase tracking-[0.24em] text-primary-foreground"
        style={{ transform: "skewX(-12deg)" }}
      >
        <Sparkles className="h-3 w-3" strokeWidth={2} />
        <span style={{ transform: "skewX(12deg)" }}>Beliebt</span>
      </span>
    )}

    <p className="font-display text-[11px] font-light uppercase tracking-[0.28em] text-light-muted">
      {plan.name}
    </p>

    <div className="mt-6 flex items-end gap-2">
      <span className="font-display text-5xl font-extralight leading-none tracking-tight text-light-foreground">
        {plan.price}&nbsp;€
      </span>
      <span className="pb-1 font-display text-sm font-light tracking-wide text-[hsl(222_80%_45%)]">
        / Monat
      </span>
    </div>

    <p className="mt-2 text-[11px] font-light text-light-muted/90">
      netto, zzgl. gesetzlicher Umsatzsteuer
    </p>

    <p className="mt-6 text-sm font-light leading-relaxed text-light-muted">
      {plan.tagline}
    </p>

    <p className="mt-6 inline-flex w-fit border border-light-border bg-[hsl(214_40%_96%)] px-3 py-1.5 font-display text-xs font-light tracking-wide text-light-foreground">
      {plan.pages}
    </p>

    <ul className="mt-7 flex flex-col gap-3 border-t border-light-border pt-7">
      {plan.features.map((feature) => (
        <li key={feature} className="flex items-start gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[hsl(222_80%_52%/0.12)] p-1">
            <Check
              className="h-3 w-3 text-[hsl(222_85%_45%)]"
              strokeWidth={2.4}
            />
          </span>
          <span className="text-sm font-light leading-relaxed text-light-muted">
            {feature}
          </span>
        </li>
      ))}
    </ul>

    <button
      type="button"
      className={`group mt-auto flex items-center justify-center gap-2 pt-9 font-display text-sm font-light tracking-wide outline-none transition-colors duration-300 ${
        plan.featured
          ? "text-[hsl(222_85%_42%)] hover:text-[hsl(222_85%_32%)]"
          : "text-light-foreground hover:text-[hsl(222_85%_42%)]"
      }`}
    >
      <span
        className={`flex w-full items-center justify-center gap-2 px-6 py-3.5 transition-[background-color,border-color,color] duration-300 ${
          plan.featured
            ? "bg-gradient-cta text-primary-foreground"
            : "border border-light-border bg-[hsl(0_0%_100%/0.6)] group-hover:border-[hsl(222_70%_60%/0.7)]"
        }`}
        style={{ transform: "skewX(-12deg)" }}
      >
        <span className="flex items-center gap-2" style={{ transform: "skewX(12deg)" }}>
          {plan.cta}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </span>
    </button>
  </article>
);

export const PricingPlans = () => {
  const [ref, inView] = useInView({ threshold: 0.08 });

  return (
    <section
      id="pakete"
      ref={ref}
      className="relative isolate overflow-hidden bg-light text-light-foreground"
      style={{
        clipPath: "polygon(0 2.8vw, 100% 0, 100% 100%, 0 calc(100% - 2.2vw))",
      }}
    >
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-light" />
      <div
        aria-hidden
        className="absolute -left-24 top-[6%] -z-10 h-[440px] w-[560px] -rotate-6 rounded-[45%] bg-[radial-gradient(closest-side,hsl(214_80%_72%/0.26),transparent)] blur-2xl"
      />
      <div
        aria-hidden
        className="absolute -right-16 bottom-[2%] -z-10 h-[420px] w-[520px] rounded-full bg-[radial-gradient(closest-side,hsl(265_70%_74%/0.2),transparent)] blur-2xl"
      />

      <div className="mx-auto max-w-[1500px] px-5 pb-28 pt-32 sm:px-8 sm:pb-32 sm:pt-36 lg:px-12">
        <div
          className={`max-w-2xl transition-[opacity,transform] duration-700 ease-out ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-light-muted">
            Unsere Pakete
          </p>
          <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-light-foreground sm:text-4xl lg:text-5xl">
            Ein Paket, das mit dir wächst.
          </h2>
          <div
            aria-hidden
            className="mt-7 h-px w-24 bg-[linear-gradient(90deg,hsl(222_90%_45%),hsl(265_70%_58%/0.15))]"
          />
          <p className="mt-7 text-sm font-light leading-relaxed text-light-muted sm:text-base">
            Alle Pakete laufen monatlich, ohne Einrichtungsgebühr und mit
            persönlichem Ansprechpartner. Du kannst jederzeit wechseln, wenn dein
            Unternehmen mehr braucht.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 lg:mt-20 lg:grid-cols-3 lg:gap-7">
          {PLANS.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              index={index}
              inView={inView}
            />
          ))}
        </div>

        <p
          className={`mt-10 text-xs font-light text-light-muted transition-opacity duration-700 ${
            inView ? "opacity-100" : "opacity-0"
          }`}
        >
          Alle Preise netto, zzgl. gesetzlicher Umsatzsteuer · monatlich kündbar
          · erster Entwurf in 14 Tagen
        </p>
      </div>
    </section>
  );
};
